import React from 'react';
import { ShieldCheck, Mail, Globe } from 'lucide-react';
import { useScrollReveal } from '../hooks/useAnimations';

const Reveal: React.FC<{ children: React.ReactNode; className?: string; delay?: number }> = ({ children, className = '', delay = 0 }) => {
  const { ref, visible } = useScrollReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`transition-all duration-[800ms] ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const Section: React.FC<{ num: number; title: string; children: React.ReactNode }> = ({ num, title, children }) => (
  <div className="mt-10">
    <h2 className="flex items-center gap-3 text-xl font-bold text-silver-900 mb-3">
      <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-brand-50 text-brand-600 text-sm font-bold flex items-center justify-center">{num}</span>
      {title}
    </h2>
    <div className="text-silver-600 text-[15px] leading-relaxed space-y-3">{children}</div>
  </div>
);

const Bullets: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="space-y-2">
    {items.map((item, i) => (
      <li key={i} className="relative pl-6">
        <span className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full bg-brand-500" />
        {item}
      </li>
    ))}
  </ul>
);

const PrivacyPolicy: React.FC = () => {
  return (
    <main className="bg-white overflow-hidden">

      {/* ═══ HERO ═══ */}
      <section className="relative pt-28 pb-24 lg:pt-36 lg:pb-32 overflow-hidden bg-silver-950">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-brand-600/20 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/3" />
        <div className="absolute inset-0 dot-grid opacity-[0.05]" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <ShieldCheck className="w-4 h-4 text-brand-400" />
              <span className="text-xs font-semibold tracking-wide text-silver-300 uppercase">Privacy Policy</span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight">Privacy Policy</h1>
            <p className="mt-4 text-silver-400 text-base max-w-xl">
              Your data is yours. We collect only what we need to serve your orders — and never use it without your consent.
            </p>
            <span className="inline-block mt-6 text-xs font-medium text-silver-300 bg-white/10 px-3 py-1.5 rounded-full">
              Last updated: 9 July 2026
            </span>
          </Reveal>
        </div>
      </section>

      {/* ═══ CONTENT CARD ═══ */}
      <section className="relative -mt-14 pb-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="animate-fade-in">
            <div className="bg-white rounded-2xl shadow-elevated border border-silver-100 p-6 sm:p-10 lg:p-14">

              <p className="text-silver-600 text-base leading-relaxed">
                This Privacy Policy explains how <strong className="text-silver-900">Arohix</strong> ("we", "us", "our")
                collects, uses, stores and protects your information when you use the{' '}
                <strong className="text-silver-900">Arohix Order</strong> mobile application (the "App"). Arohix Order
                lets shop owners and dealers order packaged-drinking-water boxes directly from their supplier.
              </p>

              <div className="mt-6 p-5 rounded-xl bg-brand-50 border-l-4 border-brand-500 text-brand-900 text-sm font-medium">
                We do not sell your data, we do not share it with advertisers, and we never use your personal
                information for any purpose without your consent.
              </div>

              <Section num={1} title="Information We Collect">
                <p>We collect only the information required to create your account and process your orders:</p>
                <h3 className="text-base font-semibold text-silver-900 mt-4 mb-2">Information you give us</h3>
                <Bullets
                  items={[
                    <><strong className="text-silver-900">Account details</strong> — your name, shop / business name, mobile number and email address, provided when you sign up.</>,
                    <><strong className="text-silver-900">Shop / delivery details</strong> — your business type and location (state, district, area) so orders reach the right place.</>,
                    <><strong className="text-silver-900">Order information</strong> — the products and quantities you order, and your order history with your supplier.</>,
                    <><strong className="text-silver-900">Communications</strong> — messages, queries or feedback you send us through the App.</>,
                  ]}
                />
                <h3 className="text-base font-semibold text-silver-900 mt-4 mb-2">Information collected automatically</h3>
                <Bullets
                  items={[
                    <><strong className="text-silver-900">Device &amp; app data</strong> — basic device type, app version and language preference, used to keep the App working correctly.</>,
                    <><strong className="text-silver-900">Connectivity status</strong> — whether you are online or offline, so the App can queue actions and sync when you reconnect.</>,
                  ]}
                />
                <p>We do <strong className="text-silver-900">not</strong> collect your precise GPS location, contacts, photos, or track you across other apps or websites.</p>
              </Section>

              <Section num={2} title="How We Use Your Information">
                <p>Your information is used strictly to run the service you signed up for:</p>
                <Bullets
                  items={[
                    'To create and manage your account and keep you signed in.',
                    'To receive, process and deliver your orders through your supplier.',
                    'To show your order history, payments due and account statements.',
                    'To send order updates and notifications you have asked for.',
                    'To respond to your queries and provide customer support.',
                    'To keep the App secure and to fix problems and improve reliability.',
                  ]}
                />
                <div className="mt-4 p-5 rounded-xl bg-brand-50 border-l-4 border-brand-500 text-brand-900 text-sm font-medium">
                  We will always ask for your permission before using your data for anything beyond the purposes
                  listed above. Consent is given when you check the Privacy Policy box at sign-up, and you may
                  withdraw it at any time by contacting us.
                </div>
              </Section>

              <Section num={3} title="Consent">
                <p>
                  By creating an account and ticking the Privacy Policy checkbox during sign-up, you consent to the
                  collection and use of your information as described in this policy. You can withdraw your consent
                  at any time by deleting your account or emailing us — after which we stop processing your personal
                  data, except where we must keep records to comply with the law.
                </p>
              </Section>

              <Section num={4} title="How We Share Information">
                <p>We share your information only when it is necessary to provide the service:</p>
                <Bullets
                  items={[
                    <><strong className="text-silver-900">With your supplier</strong> — the bottling plant / distributor you order from receives your order and delivery details so they can fulfil your orders.</>,
                    <><strong className="text-silver-900">Service providers</strong> — trusted infrastructure partners (e.g. secure cloud hosting, messaging for order notifications) who process data only on our behalf and under strict confidentiality.</>,
                    <><strong className="text-silver-900">Legal requirements</strong> — where required by law, court order, or to protect the rights, safety and property of our users or us.</>,
                  ]}
                />
                <p><strong className="text-silver-900">We never sell your personal information, and we never share it with advertisers or data brokers.</strong></p>
              </Section>

              <Section num={5} title="Data Security">
                <p>
                  We protect your data with industry-standard safeguards: encrypted connections (HTTPS/TLS),
                  token-based authentication, strict access controls, and tenant-isolated data storage so each
                  business's data is kept separate. While no system can be guaranteed 100% secure, we work
                  continuously to protect your information against unauthorised access, loss or misuse.
                </p>
              </Section>

              <Section num={6} title="Data Retention">
                <p>
                  We keep your information only for as long as your account is active or as needed to provide the
                  service and meet legal, accounting or reporting obligations. When you delete your account, we
                  remove or anonymise your personal data, except records we are legally required to retain.
                </p>
              </Section>

              <Section num={7} title="Your Rights">
                <p>You are in control of your data. You may, at any time:</p>
                <Bullets
                  items={[
                    <><strong className="text-silver-900">Access</strong> the personal information we hold about you.</>,
                    <><strong className="text-silver-900">Correct</strong> inaccurate or outdated information from your profile.</>,
                    <><strong className="text-silver-900">Delete</strong> your account and associated personal data.</>,
                    <><strong className="text-silver-900">Withdraw consent</strong> for optional data uses.</>,
                    <><strong className="text-silver-900">Object</strong> to certain processing of your data.</>,
                  ]}
                />
                <p>To exercise any of these rights, contact us using the details below.</p>
              </Section>

              <Section num={8} title="Children's Privacy">
                <p>
                  Arohix Order is a business tool intended for shop owners, dealers and distributors. It is not
                  directed at children under 18, and we do not knowingly collect personal information from children.
                </p>
              </Section>

              <Section num={9} title="Changes to This Policy">
                <p>
                  We may update this Privacy Policy from time to time. When we make material changes, we will
                  update the "Last updated" date above and, where appropriate, notify you within the App. Your
                  continued use of the App after changes take effect means you accept the updated policy.
                </p>
              </Section>

              <Section num={10} title="Contact Us">
                <p>If you have any questions about this Privacy Policy or how your data is handled, reach out:</p>
                <div className="mt-2 p-5 rounded-xl bg-silver-50 border border-silver-200 space-y-2">
                  <p className="font-semibold text-silver-900">Arohix — Arohix Order</p>
                  <p className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-brand-500 flex-shrink-0" />
                    <a href="mailto:contact@infynetics.com" className="text-brand-600 font-semibold hover:underline">contact@infynetics.com</a>
                  </p>
                  <p className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-brand-500 flex-shrink-0" />
                    <a href="https://arohix.in" className="text-brand-600 font-semibold hover:underline">arohix.in</a>
                  </p>
                </div>
              </Section>

              <hr className="my-10 border-silver-100" />
              <p className="text-xs text-silver-400">
                This policy applies to the Arohix Order consumer mobile application. It is also presented in-app on
                the sign-up screen so you can review it before creating an account.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
